import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { StoreContextService } from '../services/store-context.service';
import { StorageService, StorageKey } from '../services/storage.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // ✅ FIXED: Inject services instead of reading localStorage directly
  const authService = inject(AuthService);
  const storeContextService = inject(StoreContextService);
  const storage = inject(StorageService);
  
  let headers: Record<string, string> = {};

  // PART-3 — partner portal isolation. Partner API calls (/api/partner/...)
  // carry ONLY the partner token and NO tenant/store context headers. The
  // tenant/POS token is never attached to partner requests and the partner
  // token is never attached to anything else.
  if (req.url.includes('/api/partner/')) {
    const partnerToken = storage.getRaw(StorageKey.PARTNER_TOKEN);
    if (partnerToken) {
      return next(req.clone({ setHeaders: { Authorization: `Bearer ${partnerToken}` } }));
    }
    return next(req);
  }

  // KDS storeCode-credential isolation (POS-workspace /kds/s/{storeCode}).
  // When the kdsToken is present AND the outbound URL is one of the
  // tokenized KDS endpoints, send only the kdsToken — no tenant headers,
  // no admin/POS token. The kdsToken already carries tenant_id +
  // store_id claims, so backend scoping is correct.
  //
  // Endpoints recognised here are exactly the ones KdsScreenComponent
  // hits in storeCode-credential mode (kitchens listing is unnecessary
  // because the launch response embeds the active kitchens): the
  // original 4 (orders / clear-all / kds-start / kds-ready) plus, since
  // KC-1, the kitchen-instruction reads + acks (`kds/messages` and
  // `kds/messages/{id}/ack`). EVERY new KDS endpoint must be added here
  // or the storeCode-launched KDS sends the wrong credential. Admin-
  // launched KDS (/kds without /s/) does not store a kdsToken, so this
  // branch is skipped and the usual admin/POS token logic below runs.
  //
  // OMNI-2b.1 (F10 / E11) — the KDS 86 control calls four POS routes
  // (`/api/pos/stores/{id}/availability/{86,restore,86-tree,impact}`) and
  // they are DELIBERATELY NOT LISTED HERE. That control is admin-launched
  // only: the storeCode board holds a device token with no user, which both
  // `RequirePermission` and `PosActionGate` deny, so the screen hides the
  // control instead (KdsScreenComponent.canEightySix). Admin-launched, those
  // URLs fall through to the `/api/pos/` branch below and carry the POS or
  // admin token — a real user, which is the whole point. Adding them here
  // would attach a credential that cannot authorise them and would advertise
  // a control that must not exist on a tablet.
  //
  // Recorded for whoever revisits E11: a request from the tablet board with
  // NO usable token is not a harmless 403 — an /api/pos/ 401 carrying no
  // bearer takes error.interceptor's ADMIN branch (no POS token in the
  // header, not a /pos/ path, no standalone code) and would navigate the
  // kitchen screen to /login. The control being hidden is what prevents it.
  const isKdsApi =
    /\/api\/stores\/[^/]+\/(kds\/orders|kds\/clear-all|kds-start|kds-ready|kds\/messages(\/[^/]+\/ack)?)(\?|$)/
      .test(req.url);
  if (isKdsApi) {
    const kdsToken = storage.getRaw(StorageKey.KDS_TOKEN);
    if (kdsToken) {
      return next(req.clone({ setHeaders: { Authorization: `Bearer ${kdsToken}` } }));
    }
  }

  // CFD-1 — Customer Facing Display storeCode-credential isolation
  // (POS-workspace /customer-display/s/{storeCode}). When the cfdToken is
  // present AND the outbound URL is the tokenized trimmed check-read
  // endpoint, send ONLY the cfdToken — no tenant headers, no admin/POS
  // token. The cfdToken carries tenant_id + store_id claims and role
  // CustomerDisplay, so backend scoping + role gating are correct. The
  // launch POST is AllowAnonymous and is deliberately NOT matched here.
  const isCfdApi =
    /\/api\/public\/store\/[^/]+\/customer-display\/check\/[^/]+(\?|$)/.test(req.url);
  if (isCfdApi) {
    const cfdToken = storage.getRaw(StorageKey.CFD_TOKEN);
    if (cfdToken) {
      return next(req.clone({ setHeaders: { Authorization: `Bearer ${cfdToken}` } }));
    }
  }

  // Anonymous public surfaces (kiosk, pickup display, KDS/CFD launch) go out
  // bare: no staff bearer, no tenant/store headers. They are scoped by their
  // own public token or store code. Anything else stored in this browser — a
  // manager's back-office login, a cashier's POS_TOKEN, their ACTIVE_STORE —
  // belongs to a person, and the server would otherwise act on it: fence a
  // guest's order against that person's stores, record them as the one who
  // started a kiosk card payment, or pick up their store's test mode. The CFD
  // check-read above is the one public call that needs a token, and its own
  // branch attaches it.
  if (req.url.includes('/api/public/')) {
    return next(req);
  }

  // Phase HOTFIX-INTERCEPTOR — token routing is now driven by the API
  // URL of the outbound request, not by the browser tab's path.
  //
  // Why: the prior path-based rule (`window.location.pathname.startsWith('/pos/')`)
  // mis-routed cross-shell flows. Concrete case Phase OPT-B exposed:
  //
  //   1. Cashier (also a TenantAdmin) is at /pos/terminal with POS_TOKEN
  //      minted for tenant T_pos.
  //   2. Clicks "View Journey" in the session-orders preview → opens
  //      /tenant/checks/{id}/journey in a new tab.
  //   3. New tab's pathname is /tenant/... → old rule picks adminToken.
  //   4. The outbound API URL is /api/pos/checks/{id}/journey — a POS
  //      endpoint that scopes to the caller's tenant via EF query
  //      filter. adminToken's tenant ≠ T_pos → 404 "Check not found".
  //
  // POS endpoints (/api/pos/...) are designed for POS_TOKEN; using the
  // POS token keeps tenant + store context coherent regardless of which
  // shell the click came from. All other endpoints (admin/, tenant/,
  // platform/, reports/, masters/, integrations/, ...) keep using
  // adminToken exactly as before.
  //
  // Falls back to adminToken when POS_TOKEN is missing (e.g.
  // TenantAdmin who never PIN-logged into POS). Backend RBAC still
  // enforces the role check on each endpoint.
  const isPosApi = req.url.includes('/api/pos/');

  // HOTFIX-INTERCEPTOR-2C — some routes are hosted under /api/pos/ for
  // routing convenience but are actually back-office audit / read
  // surfaces (the controller-level Authorize whitelist excludes Cashier
  // / Waiter). Hitting them with POS_TOKEN, which is typically a
  // Cashier token, fails at the role gate before any permission logic
  // runs.
  //
  // Concrete failure mode this whitelist fixes:
  //   1. Cashier PIN-logs into POS → POS_TOKEN = Cashier JWT
  //   2. Yasir (admin, Owner) opens /tenant/.../journey in another tab
  //   3. Interceptor sees /api/pos/ in the URL → picks POS_TOKEN
  //   4. PosJourneyController allows only TenantAdmin/Owner/Manager/...
  //   5. 403 RolesAuthorizationRequirement — even though the *admin*
  //      user holds audit.view on their own token.
  //
  // The patterns below cover audit / history reads that are gated by
  // audit.view or reports.view on the backend and intended for
  // back-office investigation. Operational POS writes (orders /
  // payments / bill / reopen / void / settle) remain on POS_TOKEN so
  // the prior cross-tenant fix still holds.
  const isPosBackofficeRead =
    /\/api\/pos\/checks\/[^/]+\/journey(\/|$|\?)/.test(req.url)
    || /\/api\/pos\/sessions\/history/.test(req.url);

  // OMNI-2b.1 (I-1) — the availability routes are the FIRST /api/pos/ write
  // that a non-till surface issues, and the rule above gets them wrong there.
  //
  // PRECEDENCE, stated once so nobody has to re-derive it:
  //   • on the TILL (/pos/...), an 86 is the cashier's action and rides
  //     POS_TOKEN — unchanged. That is what carries the acting user into the
  //     audit row, and what makes the server answer `overrideEligible` so the
  //     supervisor-PIN path exists at all. Sending the manager's admin token
  //     from a till would silently authorise every cashier standing at it.
  //   • on a KDS BOARD (/kds...), an 86 is the admin user's action: an
  //     admin-launched board has no POS session of its own, and the storeCode
  //     board is refused the control outright (see the note above). Without
  //     this, a board in a browser that had also run the till sent whoever last
  //     PIN-logged in — audited to the wrong person, and liable to a
  //     StoreMismatch or a test-mode refusal that means nothing on a board.
  //
  // The surface is read from the LOCATION, deliberately, and only for these
  // routes: the KDS board is a whole tab, never a click from inside the till
  // shell, so the cross-shell mis-routing that HOTFIX-INTERCEPTOR removed
  // (a /tenant/ tab calling a /api/pos/ route) cannot arise here. Same regex
  // shape hub-base-url.interceptor.ts already uses to identify these surfaces.
  const isAvailabilityRoute = /\/api\/pos\/stores\/[^/]+\/availability\//.test(req.url);
  const isKdsSurface =
    typeof window !== 'undefined'
    && /(^|\/)kds(\/|$)/.test(window.location?.pathname ?? '');

  // ✅ FIXED: Use storage service instead of direct localStorage
  const posToken = storage.getRaw(StorageKey.POS_TOKEN);
  const adminToken = authService.token;

  // Standalone-PIN fallback — when a cashier with no admin login in
  // this browser needs to read store-scoped data from non-/api/pos/
  // endpoints (e.g. GET /api/stores/{id}/settings, /settings/currency),
  // `adminToken` is null. Without a fallback the request goes out
  // unauthenticated → 401 → ErrorInterceptor clears POS session →
  // redirect to /login. POS_TOKEN already carries tenant_id + store_id
  // + role claims and the backend role allowlists on those GET endpoints
  // already accept Cashier/Waiter/Chef, so it's a safe fallback.
  // I-1 — an availability call from a KDS board prefers the admin token, and
  // falls back to POS_TOKEN exactly as before when there is none.
  const preferAdmin = isPosBackofficeRead || (isAvailabilityRoute && isKdsSurface && !!adminToken);

  const token = (isPosApi && posToken && !preferAdmin)
    ? posToken
    : (adminToken ?? posToken);

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Tenant + store context headers.
  //
  // Phase HOTFIX-INTERCEPTOR-2 — these headers are now sent for ALL API
  // calls when activeStore is available, regardless of whether the
  // outbound URL is /api/pos/... or not. Backend resolution order
  // (TenantContextAccessor.GetStoreId / GetTenantId) is:
  //
  //     1. JWT claim (`store_id` / `tenant_id`)
  //     2. X-Store-Id / X-Tenant-Id header
  //
  // POS_TOKEN carries both claims so the headers are ignored on POS-
  // shell flows. adminToken does NOT carry a store_id claim, so when a
  // TenantAdmin calls /api/pos/... from a back-office screen (e.g. the
  // Cashier Sessions report's /api/pos/sessions/history), the X-Store-Id
  // header is the only signal the backend has for which store to scope
  // the read to. The previous `if (!isPosApi)` gate suppressed the
  // headers for /api/pos/... and broke that exact path.
  //
  // For non-POS calls behaviour is unchanged.
  const activeStore = storeContextService.activeStore;
  if (activeStore) {
    if (activeStore.tenantId) {
      headers['X-Tenant-Id'] = activeStore.tenantId;
    }
    if (activeStore.storeId) {
      headers['X-Store-Id'] = activeStore.storeId;
    }
  } else {
    // No activeStore yet — TenantAdmin who hasn't entered a store. Pass
    // tenantId from auth data so non-store-scoped endpoints (e.g.
    // /api/tenant/stores) still resolve a tenant context.
    const currentUser = authService.currentUser$;
    const authData = (currentUser as any).value;
    if (authData?.tenantId && authData?.userType !== 'PlatformAdmin') {
      headers['X-Tenant-Id'] = authData.tenantId;
    }
  }

  if (Object.keys(headers).length > 0) {
    const cloned = req.clone({ setHeaders: headers });
    return next(cloned);
  }

  return next(req);
};
